import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import styles from './ProviderCard.module.css';

export interface Provider {
  id: string;
  npi?: string;
  name: string;
  specialty?: string;
  distance?: string;
  rating?: number;
  reviewCount?: number;
  address?: string;
  acceptsInsurance?: boolean;
  nextAvailable?: string;
}

export interface ProviderCardProps {
  provider: Provider;
  variant?: 'compact' | 'standard' | 'detailed';
  showSpecialty?: boolean;
  showRating?: boolean;
  showDistance?: boolean;
  showAddress?: boolean;
  showInsurance?: boolean;
  showAvailability?: boolean;
  onClick?: (provider: Provider) => void;
  className?: string;
}

const ProviderCard: FunctionComponent<ProviderCardProps> = ({
  provider,
  variant = 'standard',
  showSpecialty = true,
  showRating = true,
  showDistance = true,
  showAddress = true,
  showInsurance = true,
  showAvailability = true,
  onClick,
  className
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(provider);
    } else {
      navigate(`/provider/${provider.npi || provider.id}`);
    }
  };

  const cardClasses = [
    styles.providerCard,
    styles[variant],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses} onClick={handleClick}>
      <div className={styles.providerInfo}>
        <div className={styles.providerHeader}>
          <PersonIcon className={styles.typeIcon} />
          <div className={styles.providerDetails}>
            <h3 className={styles.providerName}>{provider.name}</h3>
            {showSpecialty && provider.specialty && (
              <p className={styles.providerSpecialty}>{provider.specialty}</p>
            )}
          </div>
        </div>
        
        {(showDistance || showRating) && (provider.distance || provider.rating) && (
          <div className={styles.providerMeta}>
            {showDistance && provider.distance && (
              <div className={styles.metaItem}>
                <LocationOnIcon className={styles.metaIcon} />
                <span>{provider.distance}</span>
              </div>
            )}
            {showRating && provider.rating && (
              <div className={styles.metaItem}>
                <StarIcon className={styles.metaIcon} />
                <span>
                  {provider.rating} 
                  {provider.reviewCount && ` (${provider.reviewCount} reviews)`}
                </span>
              </div>
            )}
          </div>
        )}
        
        {showAddress && provider.address && (
          <p className={styles.providerAddress}>{provider.address}</p>
        )}
        
        {(showInsurance || showAvailability) && (provider.acceptsInsurance !== undefined || provider.nextAvailable) && (
          <div className={styles.providerFooter}>
            {showInsurance && provider.acceptsInsurance !== undefined && (
              <span className={styles.insuranceStatus}>
                {provider.acceptsInsurance ? 'Accepts your insurance' : 'Out of network'}
              </span>
            )}
            {showAvailability && provider.nextAvailable && (
              <span className={styles.availability}>
                Next: {new Date(provider.nextAvailable).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderCard;